<template>
  <div class="filterableMenus">
    <div class="filterableMenus__filter">
      <input v-if="filterable" v-model="search" placeholder="Filter menu" />
    </div>
    <template v-for="menu in menus" :key="menu.id">
      <!--
        `<Menu>` is resolved from the GLOBAL component registry (no local
        import), which is the whole point of this override — see the docblock.
        `v-bind="menu.props"` forwards whatever the server actually sent.
      -->
      <Menu
        :component-id="menu.id"
        :search="search"
        v-bind="menu.props"
      />
    </template>
  </div>
</template>

<script setup>
/**
 * Project-tier override of icarus-core's `nav/FilterableMenus.vue`.
 *
 * The core version is unusable for any project that customises the left nav,
 * for two independent reasons — both fixed here, and both worth fixing
 * upstream (see `backlog.md`):
 *
 *  1. **It defeats component overriding.** Core does
 *     `import Menu from "@apexl/icarus-core/src/components/nav/Menu.vue"` and
 *     renders that binding directly. A static import is resolved at build
 *     time and never consults the app's global component registry, so
 *     icarus's entire override mechanism — BOTH the project tier
 *     (`src/components/`) and the theme tier (`src/themes/<name>/`) — cannot
 *     reach the sidebar's menu renderer. Overriding `Menu.vue` appears to
 *     work (the component registers, `app._context.components.Menu` is the
 *     project one) while the sidebar keeps rendering the vendor component,
 *     which is exactly the failure v0.9.3 shipped. This version resolves
 *     `Menu` globally instead.
 *
 *  2. **It drops unrecognised props.** Core forwards exactly five
 *     (`componentId`, `menuItems`, `search`, `title`, `collapsable`), so
 *     anything a project's server-side `Menu` component sets via
 *     `setProperty()` never reaches Vue. Goshawk's
 *     `RegisterGoshawkNavGroupsHook` sets three more — `defaultCollapsed`,
 *     `groupKey` and `badges` — and `GET /` demonstrably serialises all of
 *     them into each menu's `props`. `v-bind="menu.props"` forwards the
 *     payload as sent; `search` stays explicit because it is this
 *     component's own state, not server data.
 *
 * Nothing else about the core component is changed: same root markup, same
 * filter input, same scoped styles, so the sidebar SCSS keeps matching.
 */
import commonProps from '@apexl/icarus-core/src/components/_properties/common';
import { ref } from 'vue';

defineProps({
  ...commonProps,
  menus: {
    type: Array,
    default: () => [],
  },
  filterable: {
    type: Boolean,
    default: false,
  },
});

const search = ref('');
</script>

<style lang="scss" scoped>
.filterableMenus {
  &__filter {
    padding: 0.75rem 0.75rem 0 0.75rem;

    input {
      width: 100%;
    }
  }
}
</style>
